import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { UserPlus, Loader2, Salad, MoreVertical, KeyRound, Mail, Trash2, Copy, Shuffle } from 'lucide-react';
import { toast } from 'sonner';

// V14.6 — Medical Staff management, surfaced as a tab under Users → Medical Staff.
// V64.1 — provider lifecycle: set/reset password, email a set-password link,
// deactivate (reversible) and remove from team. New consults are round-robined
// across everyone active. Consultation oversight lives under SoulAI → Oversight.
export function MedicalStaffPanel() {
  const [providers, setProviders] = useState([]);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get('/api/admin/medical/providers')
      .then(p => { setProviders(p.data.providers || []); setAvailability(p.data.availability || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleAvail = async (v) => {
    try {
      await api.put('/api/admin/medical/availability', { available: v });
      toast.success(v ? 'Nutritionist set Available' : 'Nutritionist set On-leave — SoulAI will cover');
      load();
    } catch { toast.error('Could not update'); }
  };

  const nutriAvail = availability?.nutritionist?.available;
  const activeCount = providers.filter(p => p.is_active !== false).length;

  return (
    <div className="space-y-5" data-testid="medical-staff-panel">
      {/* Availability switch (the leave switch) */}
      <Card className="rounded-2xl border-emerald-200">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base"><Salad className="h-5 w-5 text-emerald-600" />Nutritionist availability</CardTitle>
          <CardDescription>When off, SoulAI stops offering live connect and answers learners itself, telling them the nutritionist is currently unavailable.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3" data-testid="admin-availability-toggle">
            <Switch checked={!!nutriAvail} onCheckedChange={toggleAvail} />
            <span className={nutriAvail ? 'text-emerald-700 font-medium' : 'text-muted-foreground'}>{nutriAvail ? 'Available — accepting consultations' : 'On leave — SoulAI is covering'}</span>
          </div>
        </CardContent>
      </Card>

      {/* Providers + onboarding */}
      <Card className="rounded-2xl">
        <CardHeader className="pb-3 flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Providers</CardTitle>
            <CardDescription>Gynaecologist &amp; Paediatrician are coming soon.</CardDescription>
          </div>
          <OnboardDialog reload={load} />
        </CardHeader>
        <CardContent className="space-y-2">
          {activeCount > 1 && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="round-robin-note">
              <Shuffle className="h-3.5 w-3.5" />
              New consultations are shared round-robin across the {activeCount} active nutritionists — whoever has the fewest open chats gets the next one.
            </p>
          )}
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : providers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No providers yet. Add your nutritionist to go live.</p>
          ) : providers.map(p => (
            <ProviderRow key={p.id} p={p} reload={load} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function ProviderRow({ p, reload }) {
  const [busy, setBusy] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  // She may be the only nutritionist — then her open chats have nowhere to go.
  // The API says so with 409 NO_TRANSFER_TARGET and we offer to close them.
  const [conflict, setConflict] = useState(null); // { action, openConsults }
  const active = p.is_active !== false;

  const setStatus = async (v, mode = 'transfer') => {
    setBusy(true);
    try {
      const r = await api.patch(`/api/admin/medical/provider/${p.id}/status?on_open_consults=${mode}`, { is_active: v });
      toast.success(v ? `${p.display_name || p.name} is active again`
        : r.data.transferred ? `Deactivated — ${r.data.transferred} open chat(s) moved to ${r.data.to}`
          : r.data.closed ? `Deactivated — ${r.data.closed} open chat(s) closed, learners were told`
            : 'Deactivated — learners will no longer be routed to her');
      setConflict(null);
      reload();
    } catch (e) {
      // api.js flattens `detail` to a string and keeps the structured object
      // on `detailObject` — read the code from there.
      const d = e.response?.data?.detailObject;
      if (d?.code === 'NO_TRANSFER_TARGET') {
        toast.error(d.message);
        setConflict({ action: 'deactivate', openConsults: d.open_consults });
      } else {
        toast.error(e.response?.data?.detail || 'Could not update');
      }
    } finally { setBusy(false); }
  };

  const sendLink = async () => {
    setBusy(true);
    try {
      await api.post(`/api/admin/medical/provider/${p.id}/set-password-link`);
      toast.success(`Set-password link emailed to ${p.email} (valid 48h)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send the email');
    } finally { setBusy(false); }
  };

  const remove = async (mode = 'transfer') => {
    setBusy(true);
    try {
      const r = await api.delete(`/api/admin/medical/provider/${p.id}?on_open_consults=${mode}`);
      toast.success(r.data.transferred ? `Removed — ${r.data.transferred} open chat(s) moved to ${r.data.to}`
        : r.data.closed ? `Removed — ${r.data.closed} open chat(s) closed, learners were told`
          : 'Removed from the medical team. Past consultations are kept.');
      setRemoveOpen(false);
      setConflict(null);
      reload();
    } catch (e) {
      const d = e.response?.data?.detailObject;
      if (d?.code === 'NO_TRANSFER_TARGET') {
        setRemoveOpen(false);
        setConflict({ action: 'remove', openConsults: d.open_consults });
      } else {
        toast.error(e.response?.data?.detail || 'Could not remove');
      }
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-xl border ${active ? '' : 'bg-muted/40'}`} data-testid="provider-row-wrap">
    <div className="flex items-center justify-between p-3" data-testid="provider-row">
      <div className="min-w-0">
        <div className="font-medium flex items-center gap-2 flex-wrap">{p.salutation ? `${p.salutation}. ` : ''}{p.display_name || p.name}
          {p.is_verified && <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-300">Verified</Badge>}
          <Badge variant="secondary" className="text-[10px] capitalize">{p.medical_category}</Badge>
          {!p.has_password && !p.has_google && (
            <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300" data-testid="provider-no-login-badge">Can&apos;t sign in yet</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{p.email} · {p.qualification || '—'} · {p.experience_years || 0} yrs
          {active && <> · <span className="text-foreground">{p.open_consults || 0} open</span></>}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant={active ? 'default' : 'outline'}>{active ? 'Active' : 'Inactive'}</Badge>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" disabled={busy} data-testid={`provider-menu-${p.id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setPwdOpen(true)} data-testid="provider-reset-password">
              <KeyRound className="h-4 w-4 mr-2" />Set / reset password
            </DropdownMenuItem>
            <DropdownMenuItem onClick={sendLink} data-testid="provider-send-link">
              <Mail className="h-4 w-4 mr-2" />Email set-password link
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setStatus(!active)} data-testid="provider-toggle-active">
              {active ? 'Deactivate (reversible)' : 'Reactivate'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setRemoveOpen(true)} className="text-red-600" data-testid="provider-remove">
              <Trash2 className="h-4 w-4 mr-2" />Remove from team
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      </div>

      <SetPasswordDialog p={p} open={pwdOpen} setOpen={setPwdOpen} reload={reload} />

      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove {p.display_name || p.name} from the medical team?</DialogTitle>
            <DialogDescription>
              She loses provider access and will never be routed a new consultation. Her past
              consultations, messages and ratings are kept for your records.
              {p.open_consults ? ` Her ${p.open_consults} open chat(s) will be handed to the next active nutritionist.` : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => remove()} disabled={busy} data-testid="provider-remove-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Only nutritionist on the team — her open chats have nowhere to go.
          Rendered inline (not a dialog) so the admin cannot miss it. */}
      {conflict && (
        <div className="mx-3 mb-3 p-3 rounded-lg border border-amber-300 bg-amber-50" data-testid="provider-conflict-panel">
          <p className="text-sm font-medium text-amber-900">No one else can take her open chats</p>
          <p className="text-xs text-amber-900/80 mt-1 leading-relaxed">
            {p.display_name || p.name} has {conflict.openConsults} open consultation(s) and she is the only
            active nutritionist, so there is nobody to hand them to. Close those chats and each learner is
            told our nutritionist is unavailable, that SoulAI will keep helping her, and that we'll connect
            her once someone is available. Past messages are kept.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button size="sm" variant="destructive" disabled={busy} data-testid="provider-conflict-close-chats"
              onClick={() => (conflict.action === 'remove' ? remove('close') : setStatus(false, 'close'))}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                : `Close ${conflict.openConsults} chat(s) & ${conflict.action === 'remove' ? 'remove' : 'deactivate'}`}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setConflict(null)} data-testid="provider-conflict-cancel">
              Keep her active
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SetPasswordDialog({ p, open, setOpen, reload }) {
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (pwd.trim().length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setBusy(true);
    try {
      await api.post(`/api/admin/medical/provider/${p.id}/password`, { password: pwd.trim() });
      await navigator.clipboard?.writeText?.(`${p.email} / ${pwd.trim()}`).catch(() => {});
      toast.success('Password set — email and password copied to your clipboard');
      setOpen(false); setPwd(''); reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not set the password');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set password for {p.display_name || p.name}</DialogTitle>
          <DialogDescription>She signs in at /login with {p.email}. Share this with her over a private channel, and ask her to change it afterwards. She can also sign in with Google using the same email.</DialogDescription>
        </DialogHeader>
        <Input placeholder="New password (min 8 characters)" value={pwd} onChange={e => setPwd(e.target.value)} data-testid="provider-password-input" />
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="provider-password-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Copy className="h-4 w-4 mr-1" />Set &amp; copy</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OnboardDialog({ reload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: '', email: '', phone: '', qualification: '', experience_years: '', languages: '', display_name: '', salutation: 'Dt', password: '', send_link: true });
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.name.trim() || !f.email.trim()) { toast.error('Name and email required'); return; }
    if (f.password && f.password.trim().length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!f.password && !f.send_link) { toast.error('Set a password or email her a set-password link — otherwise she cannot sign in'); return; }
    setBusy(true);
    try {
      await api.post('/api/admin/medical/create', {
        name: f.name, email: f.email, phone: f.phone, category: 'nutritionist',
        salutation: f.salutation,
        qualification: f.qualification, experience_years: parseInt(f.experience_years) || 0,
        languages: f.languages.split(',').map(s => s.trim()).filter(Boolean),
        display_name: f.display_name || f.name,
        password: f.password.trim() || null,
        send_set_password_link: f.send_link,
      });
      if (f.password.trim()) {
        await navigator.clipboard?.writeText?.(`${f.email.trim()} / ${f.password.trim()}`).catch(() => {});
        toast.success('Nutritionist onboarded — login copied to your clipboard');
      } else {
        toast.success('Nutritionist onboarded — set-password link emailed to her');
      }
      setOpen(false);
      setF({ name: '', email: '', phone: '', qualification: '', experience_years: '', languages: '', display_name: '', salutation: 'Dt', password: '', send_link: true });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not onboard'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="onboard-provider-btn"><UserPlus className="h-4 w-4 mr-1" />Add Nutritionist</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Onboard Nutritionist</DialogTitle><DialogDescription>Add a nutritionist to the medical team. Give her a password now, or email her a link to set her own — she can also sign in with Google on the same email.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <div className="flex gap-2">
            <Select value={f.salutation} onValueChange={v => upd('salutation', v)}>
              <SelectTrigger className="w-28" data-testid="onboard-salutation"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Dt">Dt.</SelectItem>
                <SelectItem value="Dr">Dr.</SelectItem>
                <SelectItem value="Ms">Ms.</SelectItem>
                <SelectItem value="Mrs">Mrs.</SelectItem>
                <SelectItem value="Mr">Mr.</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Full name" value={f.name} onChange={e => upd('name', e.target.value)} data-testid="onboard-name" />
          </div>
          <Input placeholder="Display name (shown to learners, e.g. Ankita)" value={f.display_name} onChange={e => upd('display_name', e.target.value)} data-testid="onboard-display-name" />
          <Input placeholder="Email" value={f.email} onChange={e => upd('email', e.target.value)} data-testid="onboard-email" />
          <Input placeholder="Phone (+91…)" value={f.phone} onChange={e => upd('phone', e.target.value)} />
          <Input placeholder="Qualification (e.g. RD, MSc Nutrition)" value={f.qualification} onChange={e => upd('qualification', e.target.value)} />
          <div className="flex gap-2">
            <Input placeholder="Years experience" type="number" value={f.experience_years} onChange={e => upd('experience_years', e.target.value)} />
            <Input placeholder="Languages (comma-sep)" value={f.languages} onChange={e => upd('languages', e.target.value)} />
          </div>
          <Input placeholder="Password (optional, min 8 — copied for you)" value={f.password} onChange={e => upd('password', e.target.value)} data-testid="onboard-password" />
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={f.send_link} onCheckedChange={v => upd('send_link', v)} data-testid="onboard-send-link" />
            Also email her a set-password link (valid 48h)
          </label>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="onboard-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Onboard'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
